import React from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';
import { Row, Col } from 'react-bootstrap';

/**
 * CommonComponentSkeleton
 *
 * Clean skeleton for common data table components that includes:
 * - Header with title and action buttons
 * - Search bar and filter dropdown
 * - Table with rows and columns
 * - Pagination controls
 * - Action buttons (Delete, Apply, View toggles)
 */
const CommonComponentSkeleton = ({
  title = 'System Users',
  showSearch = true,
  showFilters = true,
  showActions = true,
  showPagination = true,
  rowCount = 5,
  columnCount = 5,
}) => {
  const theme = useTheme();
  
  return (
    <div className="p-3 main_content_container">
      {/* Header Section */}
      <Box className="mb-3">
        <Row className="align-items-center">
          <Col>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Icon */}
              <Skeleton variant="circular" width={24} height={24} />
              {/* Title */}
              <Skeleton variant="text" width={120} height={24} />
            </Box>
          </Col>
          <Col xs="auto">
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Import/Export Button */}
              <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
              {/* Add Button */}
              <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
            </Box>
          </Col>
        </Row>
      </Box>

      {/* Search and Filter Section */}
      {(showSearch || showFilters) && (
        <Box className="mb-2">
          <Row className="align-items-center">
            <Col md={6}>
              {showSearch && <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />}
            </Col>
            <Col md={6} className="text-end">
              {showFilters && (
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={36}
                  sx={{ borderRadius: 1, ml: 'auto', display: 'inline-block' }}
                />
              )}
            </Col>
          </Row>
        </Box>
      )}

      {/* Action Buttons Section */}
      {showActions && (
        <Box className="mb-2">
          <Row className="align-items-center">
            <Col>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Delete Button */}
                <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 1 }} />
                {/* Apply Button */}
                <Skeleton variant="rectangular" width={50} height={32} sx={{ borderRadius: 1 }} />
              </Box>
            </Col>
            <Col xs="auto">
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* View Toggle Buttons */}
                <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
              </Box>
            </Col>
          </Row>
        </Box>
      )}

      {/* Table Section */}
      <Box className="mb-2">
        <Box sx={{ 
          backgroundColor: 'background.paper', 
          borderRadius: 1, 
          border: `1px solid ${theme.palette.divider}`, 
          overflow: 'hidden' 
        }}>
          {/* Table Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`, 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
          }}>
            <Row className="align-items-center">
              {/* Checkbox column */}
              <Col xs={1}>
                <Skeleton variant="rectangular" width={16} height={16} />
              </Col>
              {/* Dynamic columns based on columnCount */}
              {Array.from({ length: columnCount }).map((_, colIndex) => (
                <Col key={colIndex} xs={Math.floor(11 / columnCount)}>
                  <Skeleton variant="text" width={`${60 + colIndex * 10}%`} height={18} />
                </Col>
              ))}
            </Row>
          </Box>

          {/* Table Rows */}
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <Box key={rowIndex} sx={{ 
              p: 2, 
              borderBottom: rowIndex < rowCount - 1 ? `1px solid ${theme.palette.divider}` : 'none' 
            }}>
              <Row className="align-items-center">
                {/* Checkbox column */}
                <Col xs={1}>
                  <Skeleton variant="rectangular" width={16} height={16} />
                </Col>
                {/* Dynamic columns based on columnCount */}
                {Array.from({ length: columnCount }).map((_, colIndex) => (
                  <Col key={colIndex} xs={Math.floor(11 / columnCount)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* First column gets an icon */}
                      {colIndex === 0 && <Skeleton variant="circular" width={16} height={16} />}
                      <Skeleton variant="text" width={`${70 + colIndex * 5}%`} height={16} />
                    </Box>
                  </Col>
                ))}
              </Row>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Pagination Section */}
      {showPagination && (
        <Box>
          <Row className="align-items-center">
            <Col>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="text" width={90} height={18} />
                <Skeleton variant="rectangular" width={50} height={28} sx={{ borderRadius: 1 }} />
              </Box>
            </Col>
            <Col xs="auto">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="text" width={70} height={18} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            </Col>
          </Row>
        </Box>
      )}
    </div>
  );
};

export default CommonComponentSkeleton;
